import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ManagedDoctorsService } from '@app/services/managed-doctors.service';
import { AdminViewAsService } from '@app/services/admin-view-as.service';
import { Observable } from 'rxjs';

export const BYPASS_ACTING_AS = new HttpContextToken<boolean>(() => false);

/**
 * Intercepts HTTP requests to add an 'actingAs' query parameter when a sub-doctor is selected,
 * or when an ADMIN is viewing a doctor read-only via AdminViewAsService.
 *
 * The interceptor checks for the following conditions:
 * - Requests that don't target an 'acting-as' endpoint variant are passed through untouched.
 * - Requests that already carry an explicit `actingAs` param (e.g. the Todos-mode fan-out across
 *   sub-doctors) are passed through untouched, so the explicit value always wins.
 * - If the request context contains the `BYPASS_ACTING_AS` flag (endpoints where the backend uses
 *   the JWT user, not the selected sub-doctor), the admin's view-as target is still applied if set,
 *   otherwise the request is passed through unmodified.
 * - Otherwise the selected sub-doctor (ManagedDoctorsService) takes priority, falling back to the
 *   admin's view-as target; if neither is set the request is passed through unmodified.
 *
 * Note: This interceptor only applies to requests that use the 'acting-as' endpoint variants.
 * Regular endpoints should not receive the actingAs parameter.
 */
@Injectable()
export class ActingAsInterceptor implements HttpInterceptor {
  private managedDoctorsService = inject(ManagedDoctorsService);
  private adminViewAsService = inject(AdminViewAsService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Only applies to endpoints that support the actingAs variant
    if (!req.url.includes('/acting-as')) {
      return next.handle(req);
    }

    // An explicit actingAs param (e.g. Todos-mode fan-out) always wins untouched
    if (req.params.has('actingAs')) {
      return next.handle(req);
    }

    if (req.context.get(BYPASS_ACTING_AS)) {
      const adminTarget = this.adminViewAsService.targetUserId();
      if (!adminTarget) {
        return next.handle(req);
      }
      return next.handle(
        req.clone({ params: req.params.set('actingAs', adminTarget) })
      );
    }

    const actingAs =
      this.managedDoctorsService.effectiveUserId() ??
      this.adminViewAsService.targetUserId();

    if (!actingAs) {
      return next.handle(req);
    }

    return next.handle(
      req.clone({
        params: req.params.set('actingAs', actingAs),
      })
    );
  }
}
